// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridToolbarContainer } from '@mui/x-data-grid';
import { RegistrarService } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Service/Registrar';
import { InstrumentConfiguration } from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Configuration/Instrument';
import { AsyncButton } from '../../../components/Button/AsyncButton';
import { SyncButton } from '../../../components/Button/SyncButton';
import Loading from '../../../components/Loading';
import PartyDisplay from '../../../components/PartyDisplay';
import { QueryTable } from '../../../components/Table/QueryTable';
import { useModuleParty } from '../../../contexts/ModuleContext';
import { useParties } from '../../../contexts/PartiesContext';
import { useExerciseSubmission } from '../../../hooks/mutations/ledger/useSubmission';
import useArchiveInstrumentConfiguration from '../../../hooks/mutations/registry/configuration/useArchiveInstrumentConfiguration';
import useMutate from '../../../hooks/mutations/useMutate';
import useRegistryInstrumentConfigurations from '../../../hooks/queries/registry/configuration/useRegistryInstrumentConfigurations';
import useRegistrarService from '../../../hooks/queries/registry/onboarding/useRegistrarService';
import useAllocationFactories from '../../../hooks/queries/registry/tokenStandard/useAllocationFactories';
import useTransferRules from '../../../hooks/queries/registry/transfer/useTransferRules';
import useIsPackageAvailableFrom from '../../../hooks/queries/settings/useIsPackageAvailableFrom';
import { useOperatorParty } from '../../../hooks/queries/utility-operator/useOperatorParty';
import RequiredCredentials from '../../../modules/Credential/RequiredCredentials';
import InstrumentIdentifiers from '../../../modules/Instrument/InstrumentIdentifiers';
import TemplateContract from '../../../utils/TemplateContract';
import {
  SUC_ARC_INST_CONF,
  ERR_ARC_INST_CONF,
  SUC_CREATE_ALLOCATION_FACTORY,
  ERR_CREATE_ALLOCATION_FACTORY,
  SUC_CREATE_TRANSFER_RULE,
  ERR_CREATE_TRANSFER_RULE,
} from '../../../utils/strings';
import CreateInstrumentConfigurationDialog from './CreateInstrumentConfigurationDialog';
import { ModifyInstrumentConfigurationDialog } from './ModifyInstrumentConfigurationDialog';
import { ModifyProvierAppRewardBeneficiariesDialog } from './ModifyProviderAppRewardBeneficiariesDialog';

type Row = TemplateContract<InstrumentConfiguration>;

type Action = {
  name: string;
  action: (r: Row) => Promise<unknown>;
};

const createHeaders: (createActions: (r: Row) => Action[]) => GridColDef<Row>[] = (
  createActions,
) => {
  const renderActionCell = (params: GridRenderCellParams<Row>) => {
    const actions = createActions(params.row);
    return (
      <>
        {actions.map(({ name, action }) => (
          <AsyncButton key={name} sx={{ mr: 1 }} onClick={() => action(params.row)}>
            {name}
          </AsyncButton>
        ))}
      </>
    );
  };

  return [
    {
      field: 'registrar',
      headerName: 'Registrar',
      renderCell: (params) => <PartyDisplay party={params.row.payload.registrar} />,
      flex: 150,
      filterable: false,
    },
    {
      field: 'instrumentId',
      headerName: 'InstrumentId',
      valueGetter: (params) => `${params.row.payload.defaultIdentifier.id}`,
      flex: 150,
      filterable: false,
    },
    {
      field: 'additionalIdentifiers',
      headerName: 'Additional Instrument Identifiers',
      renderCell: (params) => (
        <InstrumentIdentifiers instrumentIdentifiers={params.row.payload.additionalIdentifiers} />
      ),
      flex: 250,
      filterable: false,
    },
    {
      field: 'issuerRequirements',
      headerName: 'Required Credentials for Issuers',
      renderCell: (params) => (
        <RequiredCredentials credentialRequirements={params.row.payload.issuerRequirements} />
      ),
      flex: 250,
      filterable: false,
    },
    {
      field: 'holderRequirement',
      headerName: 'Required Credentials for Holders',
      renderCell: (params) => (
        <RequiredCredentials credentialRequirements={params.row.payload.holderRequirements} />
      ),
      flex: 250,
      filterable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      renderCell: (params) => renderActionCell(params),
      flex: 150,
      filterable: false,
    },
  ];
};

const Toolbar = () => {
  const [createInstrumentConfigurationIsOpen, setCreateInstrumentConfigurationIsOpen] =
    useState(false);
  const registrarService = useRegistrarService();
  const { submit: createAllocationFactory } = useExerciseSubmission(
    RegistrarService.RegistrarService_CreateAllocationFactory,
    SUC_CREATE_ALLOCATION_FACTORY,
    ERR_CREATE_ALLOCATION_FACTORY,
  );
  const { submit: createTransferRule } = useExerciseSubmission(
    RegistrarService.RegistrarService_CreateTransferRule,
    SUC_CREATE_TRANSFER_RULE,
    ERR_CREATE_TRANSFER_RULE,
  );
  const operator = useOperatorParty()?.data || '';
  const party = useModuleParty();

  const allocationFactories = useAllocationFactories(operator, party);
  const transferRules = useTransferRules(operator, party);

  const isPackageAvailable = useIsPackageAvailableFrom(
    'utility-registry-app-v0',
    [operator, party],
    '0.3.0',
  );

  const isLoading =
    registrarService.isLoading &&
    allocationFactories.isLoading &&
    transferRules.isLoading &&
    isPackageAvailable.isLoading;

  return (
    <GridToolbarContainer>
      {isLoading ? null : (
        <>
          {createInstrumentConfigurationIsOpen && (
            <CreateInstrumentConfigurationDialog
              open={createInstrumentConfigurationIsOpen}
              onClose={() => setCreateInstrumentConfigurationIsOpen(false)}
            />
          )}
          <SyncButton
            onClick={() => setCreateInstrumentConfigurationIsOpen(true)}
            disabled={!registrarService.data}
          >
            Create instrument configuration
          </SyncButton>
          {operator && registrarService.data && (
            <AsyncButton
              onClick={async () => {
                return createAllocationFactory({
                  cid: registrarService.data!.contractId,
                  argument: {},
                }).catch(console.error);
              }}
              disabled={
                !registrarService.data ||
                (allocationFactories.data?.length !== undefined &&
                  allocationFactories.data?.length > 0)
              }
            >
              Create Allocation Factory
            </AsyncButton>
          )}
          {operator && registrarService.data && isPackageAvailable.data && (
            <AsyncButton
              onClick={async () => {
                return createTransferRule({
                  cid: registrarService.data!.contractId,
                  argument: {},
                }).catch(console.error);
              }}
              disabled={
                !registrarService.data ||
                (transferRules.data?.length !== undefined && transferRules.data?.length > 0)
              }
            >
              Create Transfer Rule
            </AsyncButton>
          )}
        </>
      )}
    </GridToolbarContainer>
  );
};

export const InstrumentConfigurations = () => {
  const [modifyInstrumentConfiguration, setModifyInstrumentConfiguration] = useState<
    TemplateContract<InstrumentConfiguration> | undefined
  >();
  const [modifyAppRewardBeneficiaries, setModifyAppRewardBeneficiaries] = useState<
    TemplateContract<InstrumentConfiguration> | undefined
  >();

  const configurations = useRegistryInstrumentConfigurations();
  const registrarService = useRegistrarService();
  const archiveInstrumentConfiguration = useArchiveInstrumentConfiguration();

  const mutate = useMutate();
  const { showBoundary } = useErrorBoundary<Error>();
  const { party } = useParties();

  const archive = useCallback(
    (row: Row) => {
      if (!registrarService.data) {
        showBoundary(
          new Error(ERR_ARC_INST_CONF, { cause: 'Registrar service is not available.' }),
        );
        return Promise.resolve();
      }
      const payload = {
        registrarServiceCid: registrarService.data.contractId,
        instrumentConfigurationCid: row.contractId,
      };
      return mutate(archiveInstrumentConfiguration, payload, SUC_ARC_INST_CONF, ERR_ARC_INST_CONF);
    },
    [registrarService, archiveInstrumentConfiguration, mutate, showBoundary],
  );

  const createActions = (r: Row) => {
    const actions: Action[] = [];
    if (r.payload.provider === party) {
      actions.push({
        name: 'App Reward Beneficiaries',
        action: () => Promise.resolve(setModifyAppRewardBeneficiaries(r)),
      });
    }
    if (r.payload.registrar === party)
      actions.push(
        { name: 'Modify', action: () => Promise.resolve(setModifyInstrumentConfiguration(r)) },
        { name: 'Archive', action: archive },
      );
    return actions;
  };

  const isLoading =
    configurations.isLoading ||
    registrarService.isLoading ||
    archiveInstrumentConfiguration.isLoading;
  if (isLoading) return <Loading />;

  const hasData = !!configurations.data?.length;
  if (!hasData) return <Typography>No configurations</Typography>;

  return (
    <>
      {!!modifyInstrumentConfiguration && (
        <ModifyInstrumentConfigurationDialog
          open={!!modifyInstrumentConfiguration}
          onClose={() => setModifyInstrumentConfiguration(undefined)}
          instrumentConfiguration={modifyInstrumentConfiguration}
        />
      )}
      {!!modifyAppRewardBeneficiaries && (
        <ModifyProvierAppRewardBeneficiariesDialog
          open={!!modifyAppRewardBeneficiaries}
          intrumentConfiguration={modifyAppRewardBeneficiaries}
          onClose={() => setModifyAppRewardBeneficiaries(undefined)}
        />
      )}
      <QueryTable
        enableTableWrapper
        title="Instrument Configurations"
        variant="h3"
        columns={createHeaders(createActions)}
        rowQuery={configurations}
        rowKey={(a) => a.contractId}
        toolbar={Toolbar}
      />
    </>
  );
};
